import { useCallback } from 'react';
import { useTRPCClientAPI, useTRPCMutation } from '../../../../trpc/trpc';
import { useInvalidateOutreach } from './useInvalidateOutreach';

export interface ProfileUpdate {
  genres?: string[];
  moods?: string[];
  similarArtists?: { add?: number[]; remove?: number[] };
  markets?: string[];
  bio?: string;
  releaseStory?: Record<string, string>;
  voiceSample?: string | null;
  aiAssisted?: boolean;
  senderName?: string;
  senderPostal?: string | null;
  signature?: string | null;
}

/** Build and change the sound profile (frame b). Every change saves at once. */
export function useProfileActions(accountId: number) {
  const trpcClient = useTRPCClientAPI();
  const invalidate = useInvalidateOutreach();

  const build = useTRPCMutation({
    mutationFn: (catalogItemUuid: string | null) =>
      trpcClient.outreach.profile.build.mutate({ accountId, catalogItemUuid }),
    onSuccess: () => invalidate(['profile', 'home']),
  });
  const update = useTRPCMutation({
    mutationFn: (patch: ProfileUpdate) => trpcClient.outreach.profile.update.mutate({ accountId, ...patch }),
    onSuccess: () => invalidate(['profile', 'home', 'targets']),
  });

  const { mutate: buildMutate } = build;
  const { mutate: updateMutate } = update;
  const startBuild = useCallback(
    (catalogItemUuid: string | null = null) => buildMutate(catalogItemUuid),
    [buildMutate]
  );
  const save = useCallback((patch: ProfileUpdate) => updateMutate(patch), [updateMutate]);

  return {
    startBuild,
    isBuilding: build.isPending,
    buildError: build.error,
    save,
    isSaving: update.isPending,
    saveError: update.error,
  };
}
